import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { LandingFormDataCapture, PlanSelection } from '../types/customer-portal';
import { sessionStateManager } from '../utils/sessionStateManager';

// Plan data structure from usePlanRates hook
interface PlanOption {
  productId: number;
  productName: string;
  productLabel: string;
  maxBenefitLimit: number;
  planType: string;
  rate: {
    rateAmount: number;
    currency: {
      currencyId: number;
      currencyCode: string;
      symbol: string;
    };
  };
  benefitsCategories: Array<{
    benefitsCategory: string;
    categoryCoverage: string;
    benefits: Array<{
      benefitName: string;
      benefitCoverage: string;
    }>;
  }>;
  exclusion: string[];
  productOverview: Array<{
    productOverviewId: number;
    productId: number;
    description: string;
  }>;
}

// Premium adjustment interfaces
interface PrincipalInsuredAdjustment {
  originalPrice: number;
  adjustedPrice: number;
  age: number;
}

interface GroupMemberAdjustment {
  memberId: string;
  originalPrice: number;
  adjustedPrice: number;
  age: number;
}

interface PremiumAdjustments {
  principalInsuredAdjustment?: PrincipalInsuredAdjustment;
  groupMemberAdjustments?: GroupMemberAdjustment[];
}

// Principal data interface for dedicated principal information storage
interface PrincipalData {
  principalId?: number;
  principal_id?: number;
  email?: string;
  [key: string]: unknown;
}

interface PlanSelectionContextType {
  selectedPlan: PlanOption | null;
  setSelectedPlan: (plan: PlanOption | null) => void;
  selectedPlanId: string | null;
  setSelectedPlanId: (planId: string | null) => void;
  planSection: string | null;
  setPlanSection: (section: string | null) => void;
  isShortSecure: () => boolean;
  isFamilyPlan: () => boolean;
  premiumAdjustments: PremiumAdjustments;
  setPrincipalInsuredAdjustment: (adjustment: PrincipalInsuredAdjustment | null) => void;
  setGroupMemberAdjustment: (memberId: string, adjustment: GroupMemberAdjustment | null) => void;
  clearPremiumAdjustments: () => void;
  // Add tempData management
  tempData: unknown | null;
  setTempData: (data: unknown | null) => void;
  getTempDataValue: (key: string) => unknown;
  clearTempData: () => void;
  clearAllTravelData: () => void; // Enhanced cleanup for post-payment reset
  
  // Principal data management (separate from tempData)
  principalData: PrincipalData | null;
  setPrincipalData: (data: PrincipalData | null) => void;
  getPrincipalId: () => number | null;
  clearPrincipalData: () => void;
  
  // NEW: Optional form data transfer functionality (Phase 1 - Foundation)
  // These are completely optional and don't affect existing functionality
  landingFormData: LandingFormDataCapture | null;
  setLandingFormData: (data: LandingFormDataCapture | null) => void;
  clearLandingFormData: () => void;
  hasLandingFormData: () => boolean;
}

const PlanSelectionContext = createContext<PlanSelectionContextType | undefined>(undefined);

interface PlanSelectionProviderProps {
  children: ReactNode;
}

export const PlanSelectionProvider: React.FC<PlanSelectionProviderProps> = ({ children }) => {
  // Initialize tempData from localStorage if available
  const initializeTempData = (): unknown | null => {
    try {
      const storedTempData = localStorage.getItem('planSelectionTempData');
      if (storedTempData) {
        const parsed = JSON.parse(storedTempData);
        console.log('PlanSelectionProvider: Restored tempData from localStorage:', parsed);
        return parsed;
      }
    } catch (error) {
      console.error('PlanSelectionProvider: Error parsing tempData from localStorage:', error);
    }
    return null;
  };

  // ENHANCED: Initialize session data from sessionStateManager
  const initializeSessionData = () => {
    try {
      const sessionState = sessionStateManager.getSessionState();
      console.log('PlanSelectionProvider: Initializing from session state:', sessionState);
      
      return {
        planSelection: sessionState.planSelection || null,
        travelFormData: sessionState.travelFormData || null,
        clientInformationData: sessionState.clientInformationData || null,
        selectedPlanData: (sessionState as unknown as Record<string, unknown>).selectedPlanData || null // Cast to access custom property
      };
    } catch (error) {
      console.error('PlanSelectionProvider: Error initializing session data:', error);
      return {
        planSelection: null,
        travelFormData: null,
        clientInformationData: null,
        selectedPlanData: null
      };
    }
  };

  const sessionData = initializeSessionData();

  const [selectedPlan, setSelectedPlan] = useState<PlanOption | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  // ENHANCED: Initialize planSection from session data
  const [planSection, setPlanSection] = useState<string | null>(
    sessionData.planSelection?.section || null
  );
  const [premiumAdjustments, setPremiumAdjustments] = useState<PremiumAdjustments>({});
  const [tempData, setTempDataState] = useState<unknown | null>(initializeTempData);
  
  // Principal data state (separate from tempData)
  const [principalData, setPrincipalDataState] = useState<PrincipalData | null>(null);
  
  // NEW: Landing form data state (Phase 1 - Foundation)
  const [landingFormData, setLandingFormDataState] = useState<LandingFormDataCapture | null>(null);

  // Helper function to check if current plan is Short Secure
  const isShortSecure = useCallback(() => {
    if(!sessionData.selectedPlanData){
      return false;
    }
    const { productName } = sessionData?.selectedPlanData as {
      productName: string;
    };
    return productName.toLowerCase().includes("shortsecure");
  }, [sessionData.planSelection]);

  // Enhanced setTempData with localStorage persistence and email data preservation
  const setTempData = useCallback((data: unknown | null) => {
    console.log('PlanSelectionProvider: Setting tempData:', data);
    
    // CRITICAL FIX: Enhanced email data preservation with multiple fallback sources
    let enhancedData = data;
    if (data && typeof data === 'object') {
      // Check if email is missing from new data but exists in current tempData
      const currentEmail = tempData && typeof tempData === 'object' ?
        (tempData as Record<string, unknown>).email : null;
      const newEmail = (data as Record<string, unknown>).email;
      
      if (!newEmail && currentEmail) {
        // Preserve existing email data from current tempData
        enhancedData = {
          ...data,
          email: currentEmail
        };
        console.log('PlanSelectionProvider: Preserved email data from current tempData:', currentEmail);
      } else if (!newEmail) {
        // Try to get email from localStorage email verification data
        try {
          const localEmailVerificationData = localStorage.getItem('emailVerificationTempData');
          if (localEmailVerificationData) {
            const parsedData = JSON.parse(localEmailVerificationData);
            const localEmail = parsedData.email || parsedData.emailAddress;
            if (localEmail && localEmail.trim() !== '') {
              enhancedData = {
                ...data,
                email: localEmail
              };
              console.log('PlanSelectionProvider: Preserved email data from localStorage emailVerificationTempData:', localEmail);
            }
          }
        } catch (error) {
          console.warn('PlanSelectionProvider: Error parsing localStorage emailVerificationTempData:', error);
        }
        
        // If still no email, try sessionStorage
        if (!((enhancedData as Record<string, unknown>).email)) {
          try {
            const sessionEmailVerificationData = sessionStorage.getItem('emailVerificationTempData');
            if (sessionEmailVerificationData) {
              const parsedData = JSON.parse(sessionEmailVerificationData);
              const sessionEmail = parsedData.email || parsedData.emailAddress;
              if (sessionEmail && sessionEmail.trim() !== '') {
                enhancedData = {
                  ...data,
                  email: sessionEmail
                };
                console.log('PlanSelectionProvider: Preserved email data from sessionStorage emailVerificationTempData:', sessionEmail);
              }
            }
          } catch (error) {
            console.warn('PlanSelectionProvider: Error parsing sessionStorage emailVerificationTempData:', error);
          }
        }
        
        // If still no email, try the email backup
        if (!((enhancedData as Record<string, unknown>).email)) {
          try {
            const emailBackup = localStorage.getItem('planSelectionEmailBackup');
            if (emailBackup && emailBackup.trim() !== '') {
              enhancedData = {
                ...data,
                email: emailBackup
              };
              console.log('PlanSelectionProvider: Preserved email data from localStorage backup:', emailBackup);
            }
          } catch (error) {
            console.warn('PlanSelectionProvider: Error getting email backup from localStorage:', error);
          }
        }
      }
    }
    
    setTempDataState(enhancedData);
    
    // Enhanced localStorage persistence with email data preservation
    try {
      if (enhancedData) {
        localStorage.setItem('planSelectionTempData', JSON.stringify(enhancedData));
        console.log('PlanSelectionProvider: Persisted tempData to localStorage');
        
        // CRITICAL FIX: Also save email data separately for additional persistence
        if (enhancedData && typeof enhancedData === 'object') {
          const emailValue = (enhancedData as Record<string, unknown>).email;
          if (emailValue && typeof emailValue === 'string' && emailValue.trim() !== '') {
            localStorage.setItem('planSelectionEmailBackup', emailValue);
            console.log('PlanSelectionProvider: Saved email backup to localStorage:', emailValue);
          }
        }
      } else {
        localStorage.removeItem('planSelectionTempData');
        console.log('PlanSelectionProvider: Removed tempData from localStorage');
      }
    } catch (error) {
      console.error('PlanSelectionProvider: Error persisting tempData to localStorage:', error);
    }
  }, [tempData]);

  // Helper function to get specific values from tempData
  const getTempDataValue = useCallback((key: string): unknown => {
    if (!tempData || typeof tempData !== 'object') {
      console.log(`PlanSelectionProvider: getTempDataValue(${key}) - no tempData available`);
      return null;
    }
    const value = (tempData as Record<string, unknown>)[key] || null;
    console.log(`PlanSelectionProvider: getTempDataValue(${key}) = ${value}`);
    return value;
  }, [tempData]);

  // Helper function to check if current plan is Family Plan
  const isFamilyPlan = useCallback(() => {
    // Check multiple sources to determine if this is a family plan
    // 1. Check URL path
    const currentPath = window.location.pathname;
    if (currentPath.includes('/family')) {
      return true;
    }
    
    // 2. Check tempData for plan type information
    const tempDataTypeName = getTempDataValue('typeName') as string;
    const tempDataResolvedPlanType = getTempDataValue('resolvedPlanType') as string;
    
    if (tempDataResolvedPlanType === 'Family' || tempDataTypeName === 'Family') {
      return true;
    }
    
    // 3. Check selected plan data
    if (selectedPlan?.planType === 'Family') {
      return true;
    }
    
    return false;
  }, [getTempDataValue, selectedPlan]);

  // Use useCallback to memoize functions and prevent unnecessary re-renders
  const setPrincipalInsuredAdjustment = useCallback((adjustment: PrincipalInsuredAdjustment | null) => {
    setPremiumAdjustments(prev => {
      // Only update if the adjustment is actually different
      if (!adjustment && !prev.principalInsuredAdjustment) {
        return prev; // No change needed
      }
      
      if (adjustment && prev.principalInsuredAdjustment &&
          prev.principalInsuredAdjustment.originalPrice === adjustment.originalPrice &&
          prev.principalInsuredAdjustment.adjustedPrice === adjustment.adjustedPrice &&
          prev.principalInsuredAdjustment.age === adjustment.age) {
        return prev; // No change needed
      }

      return {
        ...prev,
        principalInsuredAdjustment: adjustment || undefined
      };
    });
  }, []);

  const setGroupMemberAdjustment = useCallback((memberId: string, adjustment: GroupMemberAdjustment | null) => {
    setPremiumAdjustments(prev => {
      const currentAdjustments = prev.groupMemberAdjustments || [];
      const existingIndex = currentAdjustments.findIndex(adj => adj.memberId === memberId);
      
      if (adjustment === null) {
        // Only update if the member actually exists
        if (existingIndex === -1) {
          return prev; // No change needed
        }
        
        // Remove adjustment for this member
        return {
          ...prev,
          groupMemberAdjustments: currentAdjustments.filter(adj => adj.memberId !== memberId)
        };
      } else {
        // Check if the adjustment is the same as existing one
        if (existingIndex >= 0) {
          const existing = currentAdjustments[existingIndex];
          if (existing.originalPrice === adjustment.originalPrice &&
              existing.adjustedPrice === adjustment.adjustedPrice &&
              existing.age === adjustment.age &&
              existing.memberId === adjustment.memberId) {
            return prev; // No change needed
          }
        }
        
        // Add or update adjustment for this member
        const newAdjustments = [...currentAdjustments];
        
        if (existingIndex >= 0) {
          newAdjustments[existingIndex] = adjustment;
        } else {
          newAdjustments.push(adjustment);
        }
        
        return {
          ...prev,
          groupMemberAdjustments: newAdjustments
        };
      }
    });
  }, []);

  const clearPremiumAdjustments = useCallback(() => {
    setPremiumAdjustments(prev => {
      // Only clear if there are actually adjustments to clear
      if (!prev.principalInsuredAdjustment && (!prev.groupMemberAdjustments || prev.groupMemberAdjustments.length === 0)) {
        return prev;
      }
      return {};
    });
  }, []);

  // ENHANCED: Effect to restore session data including planSection, selectedPlan, and email data
  useEffect(() => {
    // Only restore if we don't already have tempData
    if (!tempData) {
      try {
        // CRITICAL FIX: Enhanced email data restoration with multiple fallback sources
        const sessionTempData = sessionStorage.getItem('emailVerificationTempData');
        const localEmailTempData = localStorage.getItem('emailVerificationTempData');
        const planSelectionTempData = localStorage.getItem('planSelectionTempData');
        const emailBackup = localStorage.getItem('planSelectionEmailBackup');
        
        let emailVerificationData = null;
        let restoredEmail = null;
        
        // Priority 1: Check email verification data sources
        if (sessionTempData) {
          emailVerificationData = JSON.parse(sessionTempData);
          restoredEmail = emailVerificationData.email || emailVerificationData.emailAddress;
          console.log('PlanSelectionProvider: Found email verification data in sessionStorage:', emailVerificationData);
        } else if (localEmailTempData) {
          emailVerificationData = JSON.parse(localEmailTempData);
          restoredEmail = emailVerificationData.email || emailVerificationData.emailAddress;
          console.log('PlanSelectionProvider: Found email verification data in localStorage:', emailVerificationData);
        }
        
        // Priority 2: Check planSelectionTempData for email
        if (!restoredEmail && planSelectionTempData) {
          try {
            const parsedPlanData = JSON.parse(planSelectionTempData);
            if (parsedPlanData.email && parsedPlanData.email.trim() !== '') {
              restoredEmail = parsedPlanData.email;
              console.log('PlanSelectionProvider: Found email in planSelectionTempData:', restoredEmail);
            }
          } catch (error) {
            console.warn('PlanSelectionProvider: Error parsing planSelectionTempData:', error);
          }
        }
        
        // Priority 3: Check email backup
        if (!restoredEmail && emailBackup && emailBackup.trim() !== '') {
          restoredEmail = emailBackup;
          console.log('PlanSelectionProvider: Found email in backup storage:', restoredEmail);
        }
        
        if (emailVerificationData || restoredEmail) {
          // CRITICAL FIX: Ensure email data is preserved when restoring
          const enhancedData = {
            ...(emailVerificationData || {}),
            // Ensure email field is explicitly preserved from any source
            email: restoredEmail || (emailVerificationData?.email || emailVerificationData?.emailAddress)
          };
          console.log('PlanSelectionProvider: Restored tempData with preserved email:', enhancedData);
          setTempData(enhancedData);
        }
      } catch (error) {
        console.error('PlanSelectionProvider: Error restoring tempData from storage:', error);
      }
    }

    // ENHANCED: Restore planSection from session state if not already set
    if (!planSection && sessionData.planSelection) {
      console.log('PlanSelectionProvider: Restoring planSection from session:', sessionData.planSelection.section);
      setPlanSection(`${sessionData.planSelection.section}`);
    }

    // ENHANCED: Restore actual selectedPlan data from session if available
    if (!selectedPlan && sessionData.selectedPlanData) {
      console.log('PlanSelectionProvider: Restoring selectedPlan from session data:', sessionData.selectedPlanData);
      setSelectedPlan(sessionData.selectedPlanData as PlanOption);
    }
  }, [tempData, setTempData, planSection, selectedPlan, sessionData]);

  // Clear tempData function for cleanup
  const clearTempData = useCallback(() => {
    console.log('PlanSelectionProvider: Clearing tempData');
    setTempData(null);
  }, [setTempData]);

  // Enhanced cleanup function for post-payment reset
  const clearAllTravelData = useCallback(() => {
    console.log('PlanSelectionProvider: Clearing all travel data for post-payment reset');
    
    // Clear all context state
    setTempData(null);
    setPrincipalDataState(null);
    setPremiumAdjustments({});
    setSelectedPlan(null);
    setSelectedPlanId(null);
    setPlanSection(null);
    
    console.log('PlanSelectionProvider: All travel context data cleared');
  }, [setTempData]);

  // Principal data management functions
  const setPrincipalData = useCallback((data: PrincipalData | null) => {
    console.log('PlanSelectionProvider: Setting principalData:', data);
    setPrincipalDataState(data);
  }, []);

  const getPrincipalId = useCallback((): number | null => {
    if (!principalData) {
      console.log('PlanSelectionProvider: getPrincipalId() - no principalData available');
      return null;
    }
    
    // Try principalId first, then principal_id
    const id = principalData.principalId || principalData.principal_id || null;
    console.log('PlanSelectionProvider: getPrincipalId() =', id);
    return typeof id === 'number' ? id : null;
  }, [principalData]);

  const clearPrincipalData = useCallback(() => {
    console.log('PlanSelectionProvider: Clearing principalData');
    setPrincipalDataState(null);
  }, []);

  // NEW: Landing form data management functions (Phase 1 - Foundation)
  const setLandingFormData = useCallback((data: LandingFormDataCapture | null) => {
    console.log('PlanSelectionProvider: Setting landingFormData:', data);
    setLandingFormDataState(data);
  }, []);

  const clearLandingFormData = useCallback(() => {
    console.log('PlanSelectionProvider: Clearing landingFormData');
    setLandingFormDataState(null);
  }, []);

  const hasLandingFormData = useCallback(() => {
    return landingFormData !== null;
  }, [landingFormData]);

  // ENHANCED: Wrapper for setPlanSection to persist to session storage
  const enhancedSetPlanSection = useCallback((section: string | null) => {
    console.log('PlanSelectionProvider: Setting planSection with session persistence:', section);
    setPlanSection(section);
    
    // Persist to session storage
    try {
      const currentSessionState = sessionStateManager.getSessionState();
      const updatedPlanSelection = currentSessionState.planSelection ? {
        ...currentSessionState.planSelection,
        section: section as PlanSelection['section']
      } : null;
      
      if (updatedPlanSelection) {
        sessionStateManager.saveSessionState({
          ...currentSessionState,
          planSelection: updatedPlanSelection
        });
        console.log('PlanSelectionProvider: Persisted planSection to session storage:', section);
      }
    } catch (error) {
      console.error('PlanSelectionProvider: Error persisting planSection to session storage:', error);
    }
  }, []);

  // ENHANCED: Wrapper for setSelectedPlan to persist to session storage
  const enhancedSetSelectedPlan = useCallback((plan: PlanOption | null) => {
    console.log('PlanSelectionProvider: Setting selectedPlan with session persistence:', plan);
    setSelectedPlan(plan);
    
    // Persist to session storage if we have a plan
    if (plan) {
      try {
        const currentSessionState = sessionStateManager.getSessionState();
        const updatedPlanSelection: PlanSelection = {
          planType: plan.planType as PlanSelection['planType'],
          section: planSection as PlanSelection['section'] || 'Domestic'
        };
        
        // ENHANCED: Also store the actual selectedPlan data for restoration
        const enhancedSessionState = {
          ...currentSessionState,
          planSelection: updatedPlanSelection,
          selectedPlanData: plan // Store the complete plan data
        };
        
        sessionStateManager.saveSessionState(enhancedSessionState);
        console.log('PlanSelectionProvider: Persisted selectedPlan and plan data to session storage:', {
          planSelection: updatedPlanSelection,
          selectedPlanData: plan
        });
      } catch (error) {
        console.error('PlanSelectionProvider: Error persisting selectedPlan to session storage:', error);
      }
    }
  }, [planSection]);

  // Memoize the context value to prevent unnecessary re-renders of consumers
  const value = React.useMemo(() => ({
    selectedPlan,
    setSelectedPlan: enhancedSetSelectedPlan,
    selectedPlanId,
    setSelectedPlanId,
    planSection,
    setPlanSection: enhancedSetPlanSection,
    isShortSecure,
    isFamilyPlan,
    premiumAdjustments,
    setPrincipalInsuredAdjustment,
    setGroupMemberAdjustment,
    clearPremiumAdjustments,
    tempData,
    setTempData,
    getTempDataValue,
    clearTempData,
    clearAllTravelData, // Enhanced cleanup for post-payment reset
    // Principal data functions
    principalData,
    setPrincipalData,
    getPrincipalId,
    clearPrincipalData,
    // NEW: Landing form data functions (Phase 1 - Foundation)
    landingFormData,
    setLandingFormData,
    clearLandingFormData,
    hasLandingFormData,
  }), [
    selectedPlan,
    enhancedSetSelectedPlan,
    selectedPlanId,
    planSection,
    enhancedSetPlanSection,
    isShortSecure,
    isFamilyPlan,
    premiumAdjustments,
    setPrincipalInsuredAdjustment,
    setGroupMemberAdjustment,
    clearPremiumAdjustments,
    tempData,
    setTempData,
    getTempDataValue,
    clearTempData,
    clearAllTravelData, // Enhanced cleanup dependency
    // Principal data dependencies
    principalData,
    setPrincipalData,
    getPrincipalId,
    clearPrincipalData,
    // NEW: Landing form data dependencies
    landingFormData,
    setLandingFormData,
    clearLandingFormData,
    hasLandingFormData
  ]);

  return (
    <PlanSelectionContext.Provider value={value}>
      {children}
    </PlanSelectionContext.Provider>
  );
};

export const usePlanSelection = (): PlanSelectionContextType => {
  const context = useContext(PlanSelectionContext);
  if (context === undefined) {
    throw new Error('usePlanSelection must be used within a PlanSelectionProvider');
  }
  return context;
};

export default PlanSelectionContext;